/**
 * Readiness — Wave 2 (Signature Authenticated Routes) executive rewrite.
 *
 * Rebuilt against the LandingV2 aesthetic bar:
 *   • White surface cards with 1px `--ds-color-border` + `--ds-shadow-card`.
 *   • Warm cream page background (`--ds-color-background`).
 *   • Cards use `--ds-radius-lg` max — NO 24px+ blob radii.
 *   • Uppercase eyebrows via `text-[11px] tracking-[0.14em] font-semibold`.
 *   • Display headings use `--ds-font-display`, semibold, `letter-spacing: -0.02em`.
 *   • H1 stays `--ds-color-ink` — tier signal now surfaces as a `Badge`
 *     beside the title rather than a colored H1.
 *   • Progress bars use `--ds-radius-pill` fills on `--ds-color-surface-inset`
 *     track; category bar colors come from `--ds-color-{success,accent,warning}`.
 *   • Every `--v2-*` variable / inline hex / rgba pastel / rounded-2xl
 *     blob has been eliminated.
 *
 * Material-scope preserved:
 *   • Every `data-testid` from the previous Readiness page is preserved.
 *   • `GET /api/readiness/latest`, `POST /api/readiness/calculate`,
 *     `GET /api/site-settings`, `GET /api/credit-reports/comparison/score-history`
 *     fetches, and `<ScoreCircle />` / `<ScoreHistoryChart />` composition.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  FileUp,
  Info,
  Loader2,
  Map as MapIcon,
  RefreshCw,
  Rocket,
  Target,
  TriangleAlert,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { useFeatureFlags } from "@/feature-flags/FeatureFlagsContext";
import AppNav from "@/components/app/AppNav";
import ScoreCircle from "@/design-system/components/ScoreCircle";
import ScoreHistoryChart from "@/components/app/ScoreHistoryChart";
import { getModuleSubtitle } from "@/components/v2/moduleNavConfig";
import { Badge, Button } from "@/design-system";

const TRACK_A_CATEGORIES = [
  { key: "credit", label: "Credit", max: 65 },
  { key: "payment_history", label: "Payment History", max: 25 },
  { key: "funding_preparation", label: "Funding Preparation", max: 10 },
];

const TRACK_B_CATEGORIES = [
  { key: "credit", label: "Credit", max: 30 },
  { key: "payment_history", label: "Payment History", max: 15 },
  { key: "revenue", label: "Monthly Revenue", max: 30 },
  { key: "business_foundation", label: "Business Foundation", max: 25 },
];

const TIER_TONE = {
  funding_ready:            "success",
  almost_ready:             "accent",
  improvement_recommended:  "warning",
  repair_required:          "danger",
};

const PATH_META = {
  startup:  { icon: Rocket,     label: "Startup Funding Path" },
  business: { icon: Building2,  label: "Established Business Path" },
};

export default function Readiness() {
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calcing, setCalcing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/site-settings").then((res) => { if (!cancelled) setSiteSettings(res.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!user?.funding_profile_complete) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    api
      .get("/readiness/latest")
      .then((res) => { if (!cancelled) setAssessment(res.data); })
      .catch((err) => { if (!cancelled && err?.response?.status !== 404) setError(formatApiError(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.funding_profile_complete]);

  async function recalc() {
    setCalcing(true);
    setError("");
    try {
      const res = await api.post("/readiness/calculate");
      setAssessment(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCalcing(false);
    }
  }

  if (!user?.funding_profile_complete) {
    return (
      <Shell>
        <EmptyPanel
          title="Finish your profile to unlock this page."
          subtitle="We can't score what we don't know — head back and complete your profile first."
          ctaLabel="Complete profile"
          onCta={() => navigate("/profile")}
        />
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell>
        <div
          data-testid="readiness-loading"
          className="flex items-center justify-center min-h-[40vh]"
        >
          <Loader2
            className="w-6 h-6 animate-spin"
            style={{ color: "var(--ds-color-accent)" }}
          />
        </div>
      </Shell>
    );
  }

  if (!assessment) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <EmptyPanel
          title="No assessment yet."
          subtitle="Run your first funding readiness assessment now. We evaluate two paths in parallel — Startup and Established Business — and report the higher score."
          ctaLabel={calcing ? "Calculating…" : "Calculate my Funding Readiness Score"}
          ctaIcon={calcing ? Loader2 : Target}
          ctaSpin={calcing}
          ctaDisabled={calcing}
          onCta={recalc}
          testId="readiness-empty-cta"
          error={error}
        />
      </Shell>
    );
  }

  return (
    <Shell disclaimer={siteSettings?.disclaimer_text}>
      <Hero assessment={assessment} onRecalc={recalc} calcing={calcing} error={error} />

      {assessment.show_business_formation_cta && siteSettings && isEnabled("business_formation_link") && (
        <BusinessFormationCta settings={siteSettings} />
      )}

      <TrackComparison assessment={assessment} />

      <WinningTrackBreakdown assessment={assessment} paymentHistorySource={user?.payment_history_source} />

      {assessment.strengths?.length > 0 && (
        <NarrativeCard
          testId="readiness-strengths"
          title="Strengths"
          subtitle="What's working in your favour right now."
          icon={CheckCircle2}
          iconTone="success"
        >
          <ul className="mt-4 space-y-2.5 text-[14px] leading-relaxed text-[var(--ds-color-ink)]">
            {assessment.strengths.map((s, i) => (
              <li key={i} className="flex gap-2.5 items-start">
                <span
                  className="mt-2 w-1.5 h-1.5 rounded-[var(--ds-radius-pill)] flex-shrink-0"
                  style={{ background: "var(--ds-color-success)" }}
                />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </NarrativeCard>
      )}

      <NarrativeCard
        testId="readiness-holding-back"
        title="What's holding you back"
        subtitle="Weaknesses are areas to focus on. Blockers are concrete items that disqualify most funding partners."
        icon={TriangleAlert}
        iconTone="warning"
      >
        <div className="mt-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
            Weaknesses
          </div>
          {assessment.weaknesses?.length > 0 ? (
            <ul
              data-testid="readiness-weaknesses-list"
              className="mt-2 space-y-2.5 text-[14px] leading-relaxed text-[var(--ds-color-ink)]"
            >
              {assessment.weaknesses.map((w, i) => (
                <li key={i} className="flex gap-2.5 items-start">
                  <span
                    className="mt-2 w-1.5 h-1.5 rounded-[var(--ds-radius-pill)] flex-shrink-0"
                    style={{ background: "var(--ds-color-warning)" }}
                  />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p
              data-testid="readiness-weaknesses-empty"
              className="mt-2 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)]"
            >
              No weaknesses flagged in your latest assessment. Keep the current habits going.
            </p>
          )}
        </div>
        <div className="mt-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--ds-color-danger)" }}>
            Blockers to Funding
          </div>
          {assessment.blockers?.length > 0 ? (
            <ul
              data-testid="readiness-blockers-list"
              className="mt-2 space-y-2.5 text-[14px] leading-relaxed text-[var(--ds-color-ink)]"
            >
              {assessment.blockers.map((b, i) => (
                <li key={i} className="flex gap-2.5 items-start">
                  <AlertTriangle
                    className="w-3.5 h-3.5 mt-1 flex-shrink-0"
                    style={{ color: "var(--ds-color-danger)" }}
                    aria-hidden="true"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p
              data-testid="readiness-blockers-empty"
              className="mt-2 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)]"
            >
              No hard blockers detected. You are not being disqualified from any of the funding partners we screen against.
            </p>
          )}
        </div>
      </NarrativeCard>

      {assessment.next_steps?.length > 0 && (
        <NarrativeCard
          testId="readiness-next-steps"
          title="Recommended next steps"
          subtitle="Prioritized actions you can take this month."
          icon={Target}
          iconTone="accent"
        >
          <ol className="mt-4 space-y-3 text-[14px] leading-relaxed text-[var(--ds-color-ink)] list-decimal pl-5">
            {assessment.next_steps.map((s, i) => (
              <li key={i} className="pl-1">{s}</li>
            ))}
          </ol>
        </NarrativeCard>
      )}

      <NarrativeCard
        testId="readiness-timeline"
        title="Estimated readiness timeline"
        subtitle="Educational projection only — not a guarantee of approval or outcome."
        icon={Info}
        iconTone="accent"
      >
        <p className="mt-3 text-[14px] leading-relaxed text-[var(--ds-color-ink)]">
          {assessment.estimated_timeline}
        </p>
      </NarrativeCard>

      <ReadinessOverTime />

      <section className="grid md:grid-cols-2 gap-3">
        <BottomCtaCard
          to="/credit-improvement"
          testId="readiness-cta-improvement"
          Icon={FileUp}
          title="Review Credit Improvement findings"
          description="See every negative item detected across your three bureaus with a suggested dispute path grounded in FCRA references."
        />
        <BottomCtaCard
          to="/roadmap"
          testId="readiness-cta-roadmap"
          Icon={MapIcon}
          title="View Funding Roadmap"
          description="See your step-by-step plan tailored to your tier, with milestones and timing."
        />
      </section>
    </Shell>
  );
}

/* --------------------- Hero -------------------------------------------- */

function Hero({ assessment, onRecalc, calcing, error }) {
  const pathMeta = PATH_META[assessment.funding_path] || PATH_META.startup;
  const PathIcon = pathMeta.icon;
  const tierTone = TIER_TONE[assessment.tier] || "accent";
  const lastAt = new Date(assessment.created_at);
  return (
    <section
      data-testid="readiness-hero"
      className="rounded-[var(--ds-radius-xl)] p-6 md:p-8"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <div className="grid gap-6 md:grid-cols-[auto_1fr_auto] items-center">
        <ScoreCircle
          score={assessment.total_score}
          tier={assessment.tier}
          size={180}
          label="Readiness"
        />
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
            Financial Analyst
          </div>
          <h1
            data-testid="readiness-tier-label"
            className="mt-2 text-[var(--ds-color-ink)] leading-tight"
            style={{
              fontFamily: "var(--ds-font-display)",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
            }}
          >
            {assessment.tier_label}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge tone={tierTone} size="md">
              <span className="tabular-nums">{assessment.total_score}/100</span>
            </Badge>
            <Badge tone="neutral" size="md" data-testid="readiness-path-badge">
              <PathIcon className="w-3.5 h-3.5" aria-hidden />
              {pathMeta.label}
            </Badge>
          </div>
          <p
            data-testid="readiness-page-subtitle"
            className="mt-3 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-xl"
          >
            {getModuleSubtitle("/readiness")}
          </p>
          <p className="mt-3 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-xl">
            {assessment.estimated_timeline}
          </p>
          <p className="mt-3 text-[12px] text-[var(--ds-color-ink-subtle)]">
            Last assessed {lastAt.toLocaleString()}
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          data-testid="readiness-recalculate-btn"
          onClick={onRecalc}
          disabled={calcing}
          loading={calcing}
          className="self-start"
        >
          {calcing ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <RefreshCw className="w-4 h-4" aria-hidden />}
          {calcing ? "Recalculating…" : "Recalculate"}
        </Button>
      </div>
      {error && (
        <p
          data-testid="readiness-error"
          className="mt-4 text-[13px]"
          style={{ color: "var(--ds-color-danger)" }}
        >
          {error}
        </p>
      )}
    </section>
  );
}

/* --------------------- Business formation CTA -------------------------- */

function BusinessFormationCta({ settings }) {
  const url = settings.business_formation_referral_url;
  const partner = settings.business_formation_partner_name;
  const message = settings.business_formation_message;
  if (!url || !message) return null;
  return (
    <section
      data-testid="readiness-business-formation-cta"
      className="rounded-[var(--ds-radius-lg)] p-6 md:p-7"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <Badge tone="accent">
        <Target className="w-3 h-3" aria-hidden />
        Recommended next step
      </Badge>
      <h3
        className="mt-4 text-[var(--ds-color-ink)] leading-tight"
        style={{
          fontFamily: "var(--ds-font-display)",
          fontWeight: 600,
          letterSpacing: "-0.02em",
          fontSize: "clamp(1.25rem, 2vw, 1.5rem)",
        }}
      >
        Form your business to unlock more funding.
      </h3>
      <p className="mt-3 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-2xl">
        {message}
      </p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="readiness-business-formation-cta-btn"
        className="mt-5 inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-5 py-2.5 text-[14px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
        style={{
          background: "var(--ds-color-accent)",
          color: "var(--ds-color-accent-ink)",
          transitionDuration: "var(--ds-duration-fast)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ds-color-accent-strong)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "var(--ds-color-accent)")}
      >
        Start My Business Entity <ArrowUpRight className="w-4 h-4" aria-hidden />
      </a>
      {partner && (
        <p
          data-testid="readiness-business-formation-partner"
          className="mt-3 text-[12px] text-[var(--ds-color-ink-subtle)]"
        >
          Partner: {partner}
        </p>
      )}
    </section>
  );
}

/* --------------------- Track comparison -------------------------------- */

function TrackComparison({ assessment }) {
  const trackBApplicable = assessment.track_b_applicable;
  const winning = assessment.funding_path;
  return (
    <section data-testid="readiness-track-comparison" className="space-y-4">
      <SectionEyebrow>Track comparison</SectionEyebrow>
      <div className="grid md:grid-cols-2 gap-3">
        <TrackCard
          testId="readiness-track-a"
          icon={Rocket}
          name="Track A — Startup"
          score={assessment.track_a_score}
          isWinner={winning === "startup"}
          locked={false}
        />
        <TrackCard
          testId="readiness-track-b"
          icon={Building2}
          name="Track B — Established Business"
          score={trackBApplicable ? assessment.track_b_score : null}
          isWinner={winning === "business"}
          locked={!trackBApplicable}
          lockedNote="Available once you have an existing business on file."
        />
      </div>
    </section>
  );
}

function TrackCard({ testId, icon: Icon, name, score, isWinner, locked, lockedNote }) {
  return (
    <div
      data-testid={testId}
      data-winner={isWinner ? "true" : "false"}
      className="rounded-[var(--ds-radius-lg)] p-5 md:p-6"
      style={{
        background: "var(--ds-color-surface)",
        border: `1px solid ${isWinner ? "var(--ds-color-accent)" : "var(--ds-color-border)"}`,
        boxShadow: "var(--ds-shadow-card)",
        opacity: locked ? 0.65 : 1,
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className="inline-flex items-center justify-center w-9 h-9 rounded-[var(--ds-radius-md)]"
            style={{
              background: isWinner ? "var(--ds-color-accent-soft)" : "var(--ds-color-surface-muted)",
              color: isWinner ? "var(--ds-color-accent)" : "var(--ds-color-ink-muted)",
            }}
          >
            <Icon className="w-4 h-4" aria-hidden="true" />
          </div>
          <div>
            <div
              className="text-[var(--ds-color-ink)]"
              style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem" }}
            >
              {name}
            </div>
            {isWinner && (
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-accent)]">
                Your active path
              </div>
            )}
          </div>
        </div>
        <div
          className="tabular-nums"
          style={{
            fontFamily: "var(--ds-font-display)",
            fontWeight: 600,
            fontSize: "1.5rem",
            lineHeight: 1.1,
            color: locked ? "var(--ds-color-ink-subtle)" : isWinner ? "var(--ds-color-accent)" : "var(--ds-color-ink)",
          }}
        >
          {locked ? "—" : `${score}`}
          <span className="text-[12px] text-[var(--ds-color-ink-muted)] ml-1" style={{ fontFamily: "var(--ds-font-sans)", fontWeight: 400 }}>
            /100
          </span>
        </div>
      </div>
      {locked && lockedNote && (
        <p className="mt-3 text-[12px] leading-relaxed text-[var(--ds-color-ink-muted)]">{lockedNote}</p>
      )}
    </div>
  );
}

/* --------------------- Winning track breakdown ------------------------- */

function WinningTrackBreakdown({ assessment, paymentHistorySource }) {
  const isBusiness = assessment.funding_path === "business";
  const categories = isBusiness ? TRACK_B_CATEGORIES : TRACK_A_CATEGORIES;
  const breakdown = isBusiness ? assessment.track_b_breakdown : assessment.track_a_breakdown;
  const heading = isBusiness ? "Established Business breakdown" : "Startup track breakdown";
  return (
    <section data-testid="readiness-categories" className="space-y-4">
      <SectionEyebrow>{heading}</SectionEyebrow>
      <div className="grid gap-3">
        {categories.map(({ key, label, max }) => {
          const earned = breakdown?.[key] ?? 0;
          const explain = assessment.category_labels?.[key] ?? "";
          const pct = max > 0 ? Math.round((earned / max) * 100) : 0;
          const barTone =
            pct >= 75
              ? "var(--ds-color-success)"
              : pct >= 50
              ? "var(--ds-color-accent)"
              : "var(--ds-color-warning)";
          const numberTone =
            pct >= 75
              ? "var(--ds-color-success)"
              : pct >= 50
              ? "var(--ds-color-accent)"
              : "var(--ds-color-ink-muted)";
          return (
            <div
              key={key}
              data-testid={`readiness-cat-${key}`}
              className="rounded-[var(--ds-radius-lg)] p-5 md:p-6"
              style={{
                background: "var(--ds-color-surface)",
                border: "1px solid var(--ds-color-border)",
                boxShadow: "var(--ds-shadow-card)",
              }}
            >
              <div className="flex items-baseline justify-between gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[14px] font-medium text-[var(--ds-color-ink)]">{label}</div>
                  {key === "payment_history" && paymentHistorySource === "verified" && (
                    <Badge
                      tone="accent"
                      data-testid="readiness-payment-history-verified-pill"
                      title="Your payment-history score uses data extracted from your credit report."
                    >
                      Verified from credit report
                    </Badge>
                  )}
                  {key === "payment_history" && paymentHistorySource === "self_reported" && (
                    <Badge
                      tone="warning"
                      data-testid="readiness-payment-history-self-reported-pill"
                      title="Self-reported answer. Connect your IdentityIQ account to verify."
                    >
                      Self-reported · connect IdentityIQ to verify
                    </Badge>
                  )}
                </div>
                <div
                  className="tabular-nums"
                  style={{
                    fontFamily: "var(--ds-font-display)",
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    color: numberTone,
                  }}
                >
                  {earned}
                  <span
                    className="text-[12px] text-[var(--ds-color-ink-muted)] ml-1"
                    style={{ fontFamily: "var(--ds-font-sans)", fontWeight: 400 }}
                  >
                    /{max}
                  </span>
                </div>
              </div>
              <div
                className="mt-3 h-1.5 rounded-[var(--ds-radius-pill)] overflow-hidden"
                style={{ background: "var(--ds-color-surface-inset)" }}
              >
                <div
                  className="h-full rounded-[var(--ds-radius-pill)]"
                  style={{
                    width: `${pct}%`,
                    background: barTone,
                    transition: "width var(--ds-duration-slow) var(--ds-easing-standard)",
                  }}
                />
              </div>
              {explain && (
                <p className="mt-3 text-[12px] leading-relaxed text-[var(--ds-color-ink-muted)]">
                  {explain}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* --------------------- Shell / helpers --------------------------------- */

function Shell({ children, disclaimer }) {
  return (
    <div
      data-testid="readiness-root"
      data-page-outer="wave-2"
      className="min-h-screen bg-[var(--ds-color-background)] text-[var(--ds-color-ink)]"
    >
      <AppNav />
      <main className="max-w-5xl mx-auto px-4 md:px-8 pt-10 pb-20 space-y-10">
        {disclaimer && (
          <div
            data-testid="readiness-disclaimer"
            className="rounded-[var(--ds-radius-md)] p-4 flex gap-3"
            style={{
              background: "var(--ds-color-surface-muted)",
              border: "1px solid var(--ds-color-border)",
            }}
          >
            <Info
              className="w-4 h-4 mt-0.5 flex-shrink-0"
              style={{ color: "var(--ds-color-ink-muted)" }}
              aria-hidden="true"
            />
            <p className="text-[12px] leading-relaxed text-[var(--ds-color-ink-muted)]">{disclaimer}</p>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
      {children}
    </div>
  );
}

function NarrativeCard({ testId, title, subtitle, icon: Icon, iconTone = "accent", children }) {
  const chipBg =
    iconTone === "success"
      ? "var(--ds-color-success-soft)"
      : iconTone === "warning"
      ? "var(--ds-color-warning-soft)"
      : iconTone === "danger"
      ? "var(--ds-color-danger-soft)"
      : "var(--ds-color-accent-soft)";
  const chipFg =
    iconTone === "success"
      ? "var(--ds-color-success)"
      : iconTone === "warning"
      ? "var(--ds-color-warning)"
      : iconTone === "danger"
      ? "var(--ds-color-danger)"
      : "var(--ds-color-accent)";
  return (
    <section
      data-testid={testId}
      className="rounded-[var(--ds-radius-lg)] p-6 md:p-7"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="inline-flex items-center justify-center w-9 h-9 rounded-[var(--ds-radius-md)] flex-shrink-0"
          style={{ background: chipBg, color: chipFg }}
        >
          <Icon className="w-4 h-4" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="text-[var(--ds-color-ink)]"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.0625rem" }}
          >
            {title}
          </h3>
          {subtitle && (
            <p className="mt-1 text-[12px] leading-relaxed text-[var(--ds-color-ink-muted)]">{subtitle}</p>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

function EmptyPanel({
  title,
  subtitle,
  ctaLabel,
  ctaIcon: CtaIcon = Target,
  ctaSpin = false,
  ctaDisabled = false,
  onCta,
  testId,
  error,
}) {
  return (
    <section
      data-testid={testId || "readiness-empty"}
      className="rounded-[var(--ds-radius-lg)] p-10 text-center"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <h2
        className="text-[var(--ds-color-ink)]"
        style={{
          fontFamily: "var(--ds-font-display)",
          fontWeight: 600,
          letterSpacing: "-0.02em",
          fontSize: "clamp(1.5rem, 2.4vw, 1.875rem)",
        }}
      >
        {title}
      </h2>
      <p className="mt-3 text-[14px] max-w-xl mx-auto leading-relaxed text-[var(--ds-color-ink-muted)]">
        {subtitle}
      </p>
      {error && (
        <p className="mt-3 text-[13px]" style={{ color: "var(--ds-color-danger)" }}>
          {error}
        </p>
      )}
      <div className="mt-6 flex justify-center">
        <Button
          variant="primary"
          size="md"
          data-testid="readiness-empty-cta-btn"
          onClick={onCta}
          disabled={ctaDisabled}
        >
          <CtaIcon className={`w-4 h-4 ${ctaSpin ? "animate-spin" : ""}`} aria-hidden />
          {ctaLabel}
        </Button>
      </div>
    </section>
  );
}

function BottomCtaCard({ to, testId, Icon, title, description }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="block rounded-[var(--ds-radius-lg)] p-6 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
        transitionDuration: "var(--ds-duration-fast)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border-strong)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-border)")}
    >
      <div className="flex items-start gap-3">
        <Icon
          className="w-5 h-5 mt-1 flex-shrink-0"
          style={{ color: "var(--ds-color-accent)" }}
          aria-hidden="true"
        />
        <div>
          <div
            className="text-[var(--ds-color-ink)]"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem" }}
          >
            {title}
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)]">
            {description}
          </p>
        </div>
      </div>
    </Link>
  );
}

/* --------------------- Readiness over time ---------------------------- */

function ReadinessOverTime() {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/credit-reports/comparison/score-history")
      .then((r) => { if (!cancelled) setPoints(r.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || points.length < 2) return null;
  return (
    <section
      data-testid="readiness-over-time"
      className="rounded-[var(--ds-radius-lg)] p-6 md:p-7"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
            Over time
          </div>
          <h3
            className="mt-2 text-[var(--ds-color-ink)]"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.125rem" }}
          >
            How your readiness has changed
          </h3>
        </div>
        <Link
          to="/comparison"
          data-testid="readiness-over-time-cta"
          className="inline-flex items-center gap-1 text-[14px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2 rounded-[var(--ds-radius-sm)] px-1"
          style={{ color: "var(--ds-color-accent)", transitionDuration: "var(--ds-duration-fast)" }}
        >
          Full comparison <ArrowUpRight className="w-3 h-3" aria-hidden />
        </Link>
      </div>
      <div className="mt-4">
        <ScoreHistoryChart points={points} height={200} compact />
      </div>
    </section>
  );
}
